import { FormEvent, useState } from 'react';
import type { DistributionClient } from '../lib/api';
import type { IptvSource } from '../lib/types';
import { useI18n } from '../lib/use-i18n';

type Tab = 'm3u' | 'xtream';

/**
 * Adding a playlist. Both ways providers hand out access are first-class: a
 * plain M3U link, or Xtream panel credentials — which is what most sellers
 * actually give you, and which also unlocks the provider's own guide.
 */
export function IptvAddSource({ client, onClose, onAdded }: {
  client: DistributionClient;
  onClose: () => void;
  onAdded: (source: IptvSource) => void;
}) {
  const { t } = useI18n();
  const [tab, setTab] = useState<Tab>('m3u');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [url, setUrl] = useState('');
  const [host, setHost] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [epgUrl, setEpgUrl] = useState('');

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const source = tab === 'm3u'
        ? await client.addIptvPlaylist(url.trim(), name.trim() || undefined, epgUrl.trim() || undefined)
        : await client.addIptvXtream({
            host: host.trim(),
            username: username.trim(),
            password,
            name: name.trim() || undefined,
            epgUrl: epgUrl.trim() || undefined,
          });
      onAdded(source);
    } catch (caught) {
      // The API reports why the provider refused; showing it beats a generic
      // failure, because the fix is almost always in what the user typed.
      setError(caught instanceof Error ? caught.message : t('genericError'));
    } finally {
      setBusy(false);
    }
  }

  const canSubmit = tab === 'm3u'
    ? url.trim().length > 8
    : host.trim().length > 3 && username.trim().length > 0 && password.length > 0;

  return (
    <div className="iptv-dialog" role="dialog" aria-modal="true" aria-label={t('iptvAddPlaylist')}>
      <form className="iptv-dialog-card" onSubmit={submit}>
        <button
          type="button"
          className="release-launch-close"
          data-focusable="true"
          onClick={onClose}
          aria-label={t('close')}
        >
          ×
        </button>
        <small>IPTV</small>
        <h2>{t('iptvAddPlaylist')}</h2>

        <div className="iptv-dialog-tabs">
          <button
            type="button"
            data-focusable="true"
            className={tab === 'm3u' ? 'active' : ''}
            onClick={() => { setTab('m3u'); setError(null); }}
          >
            {t('iptvByLink')}
          </button>
          <button
            type="button"
            data-focusable="true"
            className={tab === 'xtream' ? 'active' : ''}
            onClick={() => { setTab('xtream'); setError(null); }}
          >
            {t('iptvByXtream')}
          </button>
        </div>

        {tab === 'm3u' ? (
          <label className="iptv-field">
            <span>{t('iptvPlaylistUrl')}</span>
            <input
              data-focusable="true"
              type="url"
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              placeholder="http://provider.tv/get.php?username=…&type=m3u_plus"
              autoFocus
            />
            <small>{t('iptvPlaylistUrlHint')}</small>
          </label>
        ) : (
          <>
            <label className="iptv-field">
              <span>{t('iptvHost')}</span>
              <input
                data-focusable="true"
                value={host}
                onChange={(event) => setHost(event.target.value)}
                placeholder="panel.provider.tv:8080"
                autoFocus
              />
              <small>{t('iptvHostHint')}</small>
            </label>
            <div className="iptv-field-row">
              <label className="iptv-field">
                <span>{t('iptvUsername')}</span>
                <input
                  data-focusable="true"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  autoComplete="off"
                />
              </label>
              <label className="iptv-field">
                <span>{t('password')}</span>
                <input
                  data-focusable="true"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="off"
                />
              </label>
            </div>
          </>
        )}

        <details className="iptv-advanced">
          <summary data-focusable="true">{t('iptvAdvanced')}</summary>
          <label className="iptv-field">
            <span>{t('iptvName')}</span>
            <input
              data-focusable="true"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t('iptvNamePlaceholder')}
            />
          </label>
          <label className="iptv-field">
            <span>{t('iptvEpgUrl')}</span>
            <input
              data-focusable="true"
              value={epgUrl}
              onChange={(event) => setEpgUrl(event.target.value)}
              placeholder="http://provider.tv/xmltv.php?…"
            />
            <small>{t('iptvEpgUrlHint')}</small>
          </label>
        </details>

        {error && <p className="iptv-dialog-error">{error}</p>}

        <button className="primary-action" data-focusable="true" type="submit" disabled={!canSubmit || busy}>
          {busy ? t('pleaseWait') : t('iptvAddAndImport')}
        </button>
      </form>
    </div>
  );
}
